import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Calendar, DollarSign, Code, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomBuildForm from '../components/CustomBuildForm';
import BookingModal from '../components/BookingModal';
import usePageMeta from '../hooks/usePageMeta';

const HowWeBuild = () => {
  usePageMeta({
    title: 'How We Build | 14-Day AI Office Manager Setup for Contractors',
    description:
      "From voice cloning to CRM integration (ServiceTitan, HouseCallPro, Jobber) — we build and deploy your AI Office Manager in 14 days. Here's exactly how.",
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const buildPhases = [
    {
      phase: 'Day 1-3',
      title: 'Voice, Script & Trade Intake',
      activities: ['Record your custom AI voice', 'Build your service menu & pricing logic', 'Map your dispatch zones', 'Define your qualifying questions'],
      deliverable: 'Your AI Office Manager personality + call flow'
    },
    {
      phase: 'Day 4-7',
      title: 'CRM & Dispatch Integration',
      activities: ['Connect ServiceTitan / HCP / Jobber', 'Wire up calendar & technician routing', 'Set up SMS confirmations', 'Configure after-hours rules'],
      deliverable: 'Live integration with your dispatch stack'
    },
    {
      phase: 'Day 8-10',
      title: 'Test Calls & Tuning',
      activities: ['100+ live test calls with your team', 'Voice & script refinement', 'Edge-case handling (emergencies, existing customers)', 'Review booking accuracy'],
      deliverable: 'Fully tuned AI Office Manager'
    },
    {
      phase: 'Day 11-14',
      title: 'Go Live & Handoff',
      activities: ['Port your number or forward existing line', 'Team training (CSRs, techs, owners)', 'Live dashboard walkthrough', 'Ongoing support handoff'],
      deliverable: 'AI answering every call 24/7'
    }
  ];

  const techStack = [
    { name: 'Voice AI', tools: 'Custom-cloned voice, sub-500ms response, emergency detection' },
    { name: 'Dispatch CRMs', tools: 'ServiceTitan, HouseCallPro, Jobber, FieldEdge, ServiceFusion' },
    { name: 'Telephony', tools: 'Twilio, number porting, call recording, SMS confirmations' },
    { name: 'Calendar + Booking', tools: 'Google Calendar, Outlook, technician routing by zone' }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header onOpenQuiz={() => {}} />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00E599] hover:text-[#00C985] font-body text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00E599]/10 border border-[#00E599]/20 rounded-full text-[#00E599] text-sm font-accent mb-6">
              AI Office Manager Setup & Deployment
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-[#F4F4F5] leading-tight tracking-tighter mb-6">
              From Missed Calls to
              <span className="block mt-2 gradient-text">Booked Jobs in 14 Days</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#A1A1AA] font-body leading-relaxed mb-8">
              We don't drop a chatbot on your website and call it a day. We build a full AI Office Manager — custom voice, trade-specific scripts, deep CRM integration — and have it answering calls for your shop in two weeks flat.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Start My Build
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="howwebuild-schedule-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                Schedule Discovery Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Build Philosophy */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              Why Custom Beats a $29/mo Chatbot Every Time
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-3xl mx-auto">
              Generic voice bots sound robotic, break on trade-specific questions, and don't plug into your dispatch. Ours are built for one shop: yours.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Code, title: 'Sounds Like Your Best CSR', desc: 'Cloned voice, your service menu, your qualifying questions — not a generic script' },
              { icon: Zap, title: 'Plugs Into Your Stack', desc: 'ServiceTitan, HCP, Jobber, FieldEdge, or your custom dispatch — we wire it in' },
              { icon: Check, title: 'Live in 14 Days', desc: 'Not 6 months of consulting slides. Fully answering real calls in two weeks' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass p-6 rounded-xl text-center"
              >
                <item.icon className="w-12 h-12 text-[#00E599] mx-auto mb-4" />
                <h3 className="text-xl font-headline font-bold text-[#F4F4F5] mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] font-body">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Build Timeline */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              14-Day Build Timeline
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              Day-by-day breakdown of how we go from your first strategy call to your AI Office Manager answering every ring.
            </p>
          </motion.div>

          <div className="space-y-6">
            {buildPhases.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass p-6 rounded-xl"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <span className="inline-block px-4 py-2 bg-[#00E599] text-[#050505] font-headline font-bold rounded-lg">
                      {phase.phase}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-headline font-bold text-[#F4F4F5] mb-3">{phase.title}</h3>
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      {phase.activities.map((activity, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#00E599]" />
                          <span className="text-sm text-[#A1A1AA] font-body">{activity}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <span className="text-sm font-accent text-[#00E599]">Deliverable: </span>
                      <span className="text-sm text-[#F4F4F5] font-body font-semibold">{phase.deliverable}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              Built On The Same Stack The Big Shops Pay $200K/yr For
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              We pair best-in-class voice AI with the dispatch software you already use.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {techStack.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="glass p-6 rounded-xl"
              >
                <h3 className="text-lg font-headline font-bold text-[#00E599] mb-2">{item.name}</h3>
                <p className="text-[#F4F4F5] font-body">{item.tools}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass p-10 rounded-2xl text-center"
          >
            <DollarSign className="w-12 h-12 text-[#00E599] mx-auto mb-4" />
            <h2 className="text-3xl font-headline font-bold text-[#F4F4F5] mb-4">
              Investment
            </h2>
            <div className="mb-4">
              <span className="text-4xl font-headline font-bold gradient-text">$4,500 setup + $897/mo</span>
            </div>
            <p className="text-[#00E599] font-body font-semibold mb-6">
              Average shop recovers $12K+ in missed jobs the first month
            </p>
            <p className="text-[#A1A1AA] font-body mb-8">
              One-time setup covers voice cloning, script, and full CRM integration. Monthly covers unlimited calls, hosting, model costs, and continuous tuning. 30-day money-back guarantee.
            </p>
            <Button
              onClick={() => setIsFormOpen(true)}
              size="lg"
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
            >
              Request Custom Quote
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-6">
              Ready to Stop Missing Jobs?
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body mb-8">
              Book a discovery call. We'll have a custom quote and a 14-day build plan in your inbox within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Start My Build
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="howwebuild-schedule-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                Schedule Discovery Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CustomBuildForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </div>
  );
};

export default HowWeBuild;
